import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';

/**
 * The app ships in two modes. Brand accents (indigo, violet, the status
 * colours) are shared; everything that reads as "surface", "hairline" or
 * "muted text" is defined per mode, both here in the MUI palette and as CSS
 * custom properties so component `sx` blocks can reference them directly.
 */

export const BRAND = {
  indigo: '#6366f1',
  indigoDark: '#4338ca',
  indigoLight: '#a5b4fc',
  violet: '#a855f7',
  orange: '#f97316',
  green: '#10b981',
  amber: '#fbbf24',
  red: '#ef4444',
  sky: '#38bdf8'
};

// CSS custom properties consumed across the app. Dark values are the ones the
// components were originally written with, so dark mode is unchanged.
const TOKENS = {
  dark: {
    '--fg-strong': '#e2e8f0',
    '--fg-muted': 'rgba(148,163,184,0.8)',
    '--fg-faint': 'rgba(148,163,184,0.62)',
    '--line-soft': 'rgba(148,163,184,0.3)',
    '--track': 'rgba(148,163,184,0.14)',
    '--line-1': 'rgba(255,255,255,0.06)',
    '--line-2': 'rgba(255,255,255,0.1)',
    '--surface': '#0f172a',
    '--surface-2': 'rgba(148,163,184,0.06)',
    '--surface-3': 'rgba(255,255,255,0.04)',
    '--accent-fg': '#a5b4fc',
    '--ai-accent': '#a855f7',
    '--status-green': '#22c55e',
    '--status-amber': '#fbbf24',
    '--status-red': '#ef4444',
    '--status-sky': '#38bdf8',
    '--status-slate': '#94a3b8',
    '--accent-fill': 'rgba(99,102,241,0.15)',
    '--accent-line': 'rgba(99,102,241,0.35)',
    '--page-bg': 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    '--paper-bg': 'linear-gradient(145deg, rgba(15,23,42,0.95), rgba(2,6,23,0.9))',
    '--card-bg': 'linear-gradient(135deg, rgba(15,23,42,0.9), rgba(30,41,59,0.75))',
    '--drawer-bg': 'linear-gradient(180deg, rgba(2,6,23,0.95) 0%, rgba(15,23,42,0.95) 100%)',
    '--input-bg': 'rgba(15,23,42,0.45)',
    '--dialog-bg': 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)',
    '--auth-bg': '#030712',
    '--auth-card': 'rgba(15,23,42,0.95)',
    '--auth-panel': 'rgba(15,23,42,0.7)',
    '--shadow-1': '0 25px 60px rgba(2,6,23,0.45)',
    '--shadow-2': '0 15px 50px rgba(2,6,23,0.4)'
  },
  light: {
    '--fg-strong': '#0f172a',
    '--fg-muted': 'rgba(51,65,85,0.88)',
    '--fg-faint': 'rgba(71,85,105,0.66)',
    '--line-soft': 'rgba(15,23,42,0.16)',
    '--track': 'rgba(15,23,42,0.08)',
    '--line-1': 'rgba(15,23,42,0.07)',
    '--line-2': 'rgba(15,23,42,0.12)',
    '--surface': '#ffffff',
    '--surface-2': 'rgba(15,23,42,0.035)',
    '--surface-3': 'rgba(15,23,42,0.03)',
    '--accent-fg': '#4338ca',
    '--ai-accent': '#7e22ce',
    '--status-green': '#15803d',
    '--status-amber': '#b45309',
    '--status-red': '#b91c1c',
    '--status-sky': '#0369a1',
    '--status-slate': '#475569',
    '--accent-fill': 'rgba(99,102,241,0.1)',
    '--accent-line': 'rgba(99,102,241,0.3)',
    '--page-bg': 'linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)',
    '--paper-bg': 'linear-gradient(145deg, #ffffff, #fbfcfe)',
    '--card-bg': 'linear-gradient(135deg, #ffffff, #f8fafc)',
    '--drawer-bg': 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    '--input-bg': '#ffffff',
    '--dialog-bg': 'linear-gradient(180deg, #ffffff 0%, #f5f3ff 100%)',
    '--auth-bg': '#f6f7fb',
    '--auth-card': '#ffffff',
    '--auth-panel': 'rgba(255,255,255,0.75)',
    '--shadow-1': '0 18px 40px rgba(15,23,42,0.08)',
    '--shadow-2': '0 10px 30px rgba(15,23,42,0.07)'
  }
};

const BODY_BACKGROUND = {
  dark: `
  radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.25), transparent 45%),
  radial-gradient(circle at 80% 0%, rgba(236, 72, 153, 0.35), transparent 40%),
  radial-gradient(circle at 50% 100%, rgba(14, 165, 233, 0.2), transparent 45%),
  linear-gradient(180deg, #020617 0%, #05060a 100%)
`,
  light: `
  radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.14), transparent 45%),
  radial-gradient(circle at 80% 0%, rgba(236, 72, 153, 0.12), transparent 40%),
  radial-gradient(circle at 50% 100%, rgba(14, 165, 233, 0.12), transparent 45%),
  linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)
`
};

export const themeTokens = (mode) => TOKENS[mode] || TOKENS.dark;

/** CSS custom properties for the active mode, ready for <GlobalStyles>. */
export const themeCssVars = (mode) => ({ ':root': themeTokens(mode) });

export const createAppTheme = (mode = 'dark') => {
  const isDark = mode === 'dark';
  const token = themeTokens(mode);

  let theme = createTheme({
    palette: {
      mode,
      primary: {
        main: BRAND.indigo,
        light: BRAND.indigoLight,
        dark: BRAND.indigoDark
      },
      secondary: {
        main: BRAND.orange,
        light: '#fdba74',
        dark: '#c2410c'
      },
      success: { main: BRAND.green },
      warning: { main: BRAND.amber },
      error: { main: BRAND.red },
      info: { main: BRAND.sky },
      background: {
        default: isDark ? '#030712' : '#f6f7fb',
        paper: isDark ? 'rgba(15, 23, 42, 0.92)' : '#ffffff'
      },
      text: {
        primary: isDark ? '#e2e8f0' : '#0f172a',
        secondary: isDark ? '#94a3b8' : '#475569'
      },
      divider: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.1)'
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 500 },
      button: { fontWeight: 600 }
    },
    shape: {
      borderRadius: 18
    },
    shadows: Array(25).fill('none'),
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundImage: BODY_BACKGROUND[mode] || BODY_BACKGROUND.dark,
            backgroundAttachment: 'fixed',
            minHeight: '100vh',
            color: isDark ? '#e2e8f0' : '#0f172a',
            WebkitFontSmoothing: 'antialiased',
            transition: 'background-color 0.25s ease, color 0.25s ease'
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(16px)'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: token['--paper-bg'],
            border: `1px solid ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.08)'}`,
            boxShadow: token['--shadow-1'],
            backdropFilter: isDark ? 'blur(18px)' : 'none'
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: `1px solid ${isDark ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.09)'}`,
            background: token['--card-bg'],
            boxShadow: token['--shadow-2']
          }
        }
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: {
            borderRadius: 999,
            textTransform: 'none',
            paddingInline: 20,
            paddingBlock: 10,
            '@media (max-width: 600px)': {
              minHeight: 44
            }
          },
          contained: {
            boxShadow: isDark
              ? '0 12px 30px rgba(99,102,241,0.35)'
              : '0 10px 24px rgba(99,102,241,0.25)'
          },
          outlined: {
            borderColor: isDark ? 'rgba(148,163,184,0.35)' : 'rgba(15,23,42,0.18)'
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: token['--input-bg'],
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(99,102,241,0.5)'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(99,102,241,0.8)'
            }
          },
          notchedOutline: {
            borderColor: isDark ? 'rgba(148,163,184,0.3)' : 'rgba(15,23,42,0.18)'
          },
          input: {
            fontWeight: 500,
            /* Prevent iOS Safari from zooming on input focus (requires >= 16px) */
            '@media (max-width: 600px)': {
              fontSize: '16px'
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '@media (max-width: 600px)': {
              fontSize: '16px'
            }
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: token['--drawer-bg'],
            borderRight: `1px solid ${isDark ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.08)'}`
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            marginInline: 8,
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              backgroundColor: alpha(BRAND.indigo, isDark ? 0.2 : 0.12)
            }
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            height: 10
          },
          bar: {
            borderRadius: 999
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.08)'}`
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#1e293b' : '#0f172a',
            fontSize: '0.75rem'
          }
        }
      }
    }
  });

  return responsiveFontSizes(theme);
};

export default createAppTheme;
